/**
 * ISS-031: typed-confirmation dialog for dead-lettering a stalled
 * outbox row. Same pattern as `QrHardDeleteDialog` — operator must
 * type `dead-letter` exactly to arm the destructive button. Optional
 * reason captured for audit metadata.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const TRIGGER = 'dead-letter';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
  submitting?: boolean;
}

export function DeadLetterConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  submitting = false,
}: Props) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');

  // Reset on close — done in the onOpenChange handler to avoid setState-
  // in-effect (eslint react-hooks/set-state-in-effect).
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setTyped('');
      setReason('');
    }
    onOpenChange(next);
  };

  const armed = typed.trim().toLowerCase() === TRIGGER;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Dead-letter this row?</DialogTitle>
          <DialogDescription>
            Sets <code>failed_permanently_at = now()</code>. The drain worker will stop
            retrying this row. Cannot be undone — the row stays in the queue tagged DLQ
            but is no longer processed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="dlq-confirm">
              Type <code className="text-[#E0556C]">{TRIGGER}</code> to confirm
            </Label>
            <Input
              id="dlq-confirm"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              spellCheck={false}
              autoComplete="off"
              placeholder={TRIGGER}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dlq-reason">Reason (optional, audit-logged)</Label>
            <Input
              id="dlq-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Sales-side schema migration in progress"
              maxLength={2000}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!armed || submitting}
            onClick={() => onConfirm(reason.trim())}
          >
            {submitting ? 'Dead-lettering…' : 'Dead-letter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
